import {
  ApostleClass,
  ApostleClassType,
  ApostleLevelsMatrix,
  ApostleLevelsMatrixPool,
  ApostleStatsMatrix,
  ApostleStatsMatrixPool
} from '@apeiron/apostle'
import * as R from 'ramda'
import { useCallback } from 'react'

const useMakeApostle = (): Hook => {
  const makeApostleStatsBaseValue = useCallback(
    (input: any): ApostleLevelsMatrix => {
      const attributes = R.propOr({}, 'attr', input)

      // Currently using the last item in level.json as the base value
      const greatestIVIndex =
        R.length(R.propOr([], 'atk', attributes) as number[]) - 1

      return {
        attack: R.pathOr(0, ['atk', greatestIVIndex], attributes),
        attackRange: R.pathOr(0, ['atk_range', greatestIVIndex], attributes),
        attackSpeed: R.pathOr(0, ['atk_spd', greatestIVIndex], attributes),
        criticalChance: R.pathOr(
          0,
          ['critical_chance', greatestIVIndex],
          attributes
        ),
        criticalDamage: R.pathOr(
          0,
          ['critical_damage', greatestIVIndex],
          attributes
        ),
        defense: R.pathOr(0, ['def', greatestIVIndex], attributes),
        health: R.pathOr(0, ['hp', greatestIVIndex], attributes),
        intelligence: R.pathOr(
          0,
          ['intelligence', greatestIVIndex],
          attributes
        ),
        moveSpeed: R.pathOr(0, ['movement_speed', greatestIVIndex], attributes),
        resistance: R.pathOr(0, ['res', greatestIVIndex], attributes)
      }
    },
    []
  )

  // private
  const makeApostleStatsMatrix = useCallback(
    (input: any): ApostleStatsMatrix => {
      const attributes = R.propOr({}, 'attr', input)

      return {
        attack: R.propOr([], 'atk', attributes),
        attackRange: R.propOr([], 'atk_range', attributes),
        attackSpeed: R.propOr([], 'atk_spd', attributes),
        criticalChance: R.propOr([], 'critical_chance', attributes),
        criticalDamage: R.propOr([], 'critical_damage', attributes),
        defense: R.propOr([], 'def', attributes),
        health: R.propOr([], 'hp', attributes),
        intelligence: R.propOr([], 'intelligence', attributes),
        moveSpeed: R.propOr([], 'movement_speed', attributes),
        resistance: R.propOr([], 'res', attributes)
      }
    },
    []
  )

  // public
  const makeApostleClass = useCallback((input: any): ApostleClass => {
    return {
      icon: R.propOr('', 'icon', input),
      name: R.propOr('', 'name', input),
      type: R.propOr('', 'type', input)
    }
  }, [])

  // public
  const makeApostleBaseValuePool = useCallback(
    (input: any): ApostleLevelsMatrixPool => {
      const ivList = R.propOr({}, 'data', input) as Object

      const ivValues = Object.values(ivList)

      const statScaleList = R.map((item: any) => {
        const className = R.propOr(
          ApostleClassType.Bard,
          'unit_class',
          item
        ) as ApostleClassType

        return {
          [className]: makeApostleStatsBaseValue(item)
        }
      }, ivValues)

      const result = R.mergeAll(statScaleList)

      return result
    },
    [makeApostleStatsBaseValue]
  )

  // public
  const makeApostleStatsMatrixPool = useCallback(
    (input: any): ApostleStatsMatrixPool => {
      const ivList = R.propOr({}, 'data', input) as Object

      const ivValues = Object.values(ivList)

      const statScaleList = R.map((item: any) => {
        const className = R.propOr(
          ApostleClassType.Bard,
          'unit_class',
          item
        ) as ApostleClassType

        return {
          [className]: makeApostleStatsMatrix(item)
        }
      }, ivValues)

      const result = R.mergeAll(statScaleList)

      return result
    },
    [makeApostleStatsMatrix]
  )

  return {
    makeApostleClass,
    makeApostleBaseValuePool,
    makeApostleStatsMatrixPool
  }
}

type Hook = {
  makeApostleClass: (input: any) => ApostleClass
  makeApostleBaseValuePool: (input: any) => ApostleLevelsMatrixPool
  makeApostleStatsMatrixPool: (input: any) => ApostleStatsMatrixPool
}

export default useMakeApostle
